/**
 * A1-93 — which of the calculation's inputs no longer match the form.
 *
 * Pure logic, shared by workflowState.js (step gating) and the
 * calculation identity header (the operator-facing reasons). Before
 * A1-93 only files + period (+ client, in WorkflowContext) were compared,
 * and a mismatch silently DROPPED the calc. Exchange rates and the country
 * filter were not compared at all, so an FX edit after a calculation left
 * every step ticked and sign-off/lock proceeded on the old rates.
 *
 * Every input the calc doc records is compared here: files (run_ids),
 * period, client, country filter, period FX and monthly FX. A comparison
 * is skipped only when one side does not carry the field at all (a calc
 * doc older than the field, or a caller that does not pass it) — never
 * guessed. FX is compared by VALUE (number), not text: the backend stores
 * "0.8650" as "0.865", and a blank grid cell is not a rate.
 */
import { normalizePeriodFxRates } from "../lib/periodFxRates.js";

export const sameSet = (a, b) => {
  const A = new Set(a || []);
  const B = new Set(b || []);
  if (A.size !== B.size) return false;
  for (const x of A) if (!B.has(x)) return false;
  return true;
};

export const sameDate = (a, b) => {
  if (!a || !b) return false;
  return String(a).slice(0, 10) === String(b).slice(0, 10);
};

const rateNum = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** {"EUR<USD": 1.1, …} — period rates by target<source, blanks dropped. */
export const canonicalPeriodFx = (raw, regime) => {
  const out = {};
  for (const [tgt, srcs] of Object.entries(normalizePeriodFxRates(raw, regime))) {
    for (const [src, r] of Object.entries(srcs || {})) {
      const n = rateNum(r);
      if (n !== null) out[`${tgt}<${src}`] = n;
    }
  }
  return out;
};

/** {"USD>EUR@2025-03": "0.92|ECB", …} — monthly rates, blanks dropped.
 *  The source text is compared too: it is the citation frozen with the
 *  filing as audit evidence, so changing it is a change to the record. */
export const canonicalMonthlyFx = (raw) => {
  const out = {};
  for (const [src, tgts] of Object.entries(raw || {})) {
    if (!tgts || typeof tgts !== "object") continue;
    for (const [tgt, months] of Object.entries(tgts)) {
      if (!months || typeof months !== "object") continue;
      for (const [m, entry] of Object.entries(months)) {
        const obj = entry && typeof entry === "object";
        const n = rateNum(obj ? entry.rate : entry);
        const source = obj ? String(entry.source || "").trim() : "";
        if (n !== null) out[`${String(src).toUpperCase()}>${String(tgt).toUpperCase()}@${m}`] = `${n}|${source}`;
      }
    }
  }
  return out;
};

const sameMap = (a, b) => {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => b[k] === a[k]);
};

/** Ordered list of changed inputs: "files" | "period" | "client" |
 *  "country_filter" | "fx". Empty when the calc still matches the form
 *  (or there is no calc). */
export function calcInputChanges(input) {
  const {
    calc = null, selectedRunIds = [], periodStart = "", periodEnd = "",
    clientId, calcClientId, countryFilter, periodFxRates, monthlyFxRates,
  } = input || {};
  if (!calc) return [];
  const out = [];
  if (!sameSet(calc.run_ids, selectedRunIds)) out.push("files");
  if (!sameDate(calc.period_start, periodStart) || !sameDate(calc.period_end, periodEnd))
    out.push("period");
  if (clientId !== undefined && calcClientId !== undefined
      && (calcClientId || null) !== (clientId || null)) out.push("client");
  if (countryFilter !== undefined && calc.country_filter !== undefined
      && String(calc.country_filter || "ALL").toUpperCase()
        !== String(countryFilter || "ALL").toUpperCase()) out.push("country_filter");
  const periodFxChanged = periodFxRates !== undefined && calc.period_fx_rates !== undefined
    && !sameMap(canonicalPeriodFx(calc.period_fx_rates, calc.regime),
      canonicalPeriodFx(periodFxRates, calc.regime));
  const monthlyFxChanged = monthlyFxRates !== undefined && calc.monthly_fx_rates !== undefined
    && !sameMap(canonicalMonthlyFx(calc.monthly_fx_rates), canonicalMonthlyFx(monthlyFxRates));
  if (periodFxChanged || monthlyFxChanged) out.push("fx");
  return out;
}

// Operator words for each changed input (no engine vocabulary).
export const INPUT_CHANGE_TEXT = Object.freeze({
  files: "The files ticked for this calculation have changed",
  period: "The filing period has changed",
  client: "A different client is selected",
  country_filter: "The country filter has changed",
  fx: "Exchange rates have changed",
});
